import { useEffect, useState, useMemo, useRef } from 'react';
import { apiFetch } from '../../api';
import Layout from '../../layouts/Layout';
import './Products.css';
import CsvImportModal from '../../components/CsvImportModal';
import DateTimeText from '../../components/DateTimeText';
import Papa from "papaparse";
import { FaFilter } from 'react-icons/fa';
import Pagination from "../../components/Pagination";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const initialFormState = {
  id: null,
  item_code: '',
  name: '',
  net_weight_per_unit: '',
  uom: '',
  test_type: '',
  status: 0,
};

export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [showCsvModal, setShowCsvModal] = useState(false);
  const [importing, setImporting] = useState(false);

    // Filter states
  const [showInvoiceFilter, setShowInvoiceFilter] = useState(false);
  const [showCustomerFilter, setShowCustomerFilter] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const totalItems = useMemo(() => {
    return filteredRows.length;
  }, [filteredRows]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / rowsPerPage);
  }, [totalItems, rowsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handlePaginatedUsers = useMemo(() => {
    return filteredRows.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  }, [filteredRows, currentPage, rowsPerPage]);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await apiFetch('/api/item-master/fetch-item-list');
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setItems(list);
      setFilteredRows(list);
    } catch (err) {
      console.error('Failed to load items', err);
      setItems([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };


    /* FILTER LOGIC
     ========================= */
  useEffect(() => {
    let filtered = items;

    if (invoiceFilter) {
      filtered = filtered.filter(row =>
        row.item_code?.toLowerCase().includes(invoiceFilter.toLowerCase())
      );
    }

    if (customerFilter) {
      filtered = filtered.filter(row =>
        row.name?.toLowerCase().includes(customerFilter.toLowerCase())
      );
    }

    setFilteredRows(filtered);
  }, [invoiceFilter, customerFilter, items]);

  const handleInvoiceFilterChange = (e) => {
    setInvoiceFilter(e.target.value);
  };

  const handleCustomerFilterChange = (e) => {
    setCustomerFilter(e.target.value);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setIsEdit(false);
    setFormData(initialFormState);
    setShowModal(true);
  };

  const openEditModal = item => {
    setIsEdit(true);
    setFormData({
      id: item.id,
      item_code: item.item_code,
      name: item.name || item.item_name,
      net_weight_per_unit: item.net_weight_per_unit,
      uom: item.uom,
      test_type: item.test_type || '',
      status: item.status,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if(!formData.item_code){return;}
    try {
      setSaving(true);
      await apiFetch('/api/item-master/create-item', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      setShowModal(false);
      setFormData(initialFormState);
      fetchItems();
    } catch (err) {
      console.error('Save failed', err);
      alert('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  /* ================= STATUS TOGGLE ================= */
  const handleStatusToggle = async (item) => {
    const newStatus = item.status === 0 ? 1 : 0;
    setItems(prev =>
      prev.map(it =>
        it.id === item.id ? { ...it, status: newStatus } : it
      )
    );
    try {
      await apiFetch('/api/item-master/change-status', {
        method: 'POST',
        body: JSON.stringify({
          id: item.id,
          status: newStatus,
        }),
      });
    } catch (err) {
      setItems(prev =>
        prev.map(it =>
          it.id === item.id ? { ...it, status: item.status } : it
        )
      );
      alert('Failed to update status');
    }
  };

  /* ================= CSV IMPORT ================= */
  // Proper CSV parser that handles quoted fields with commas
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);

    // Detect delimiter (tab or comma)
    const delimiter = lines[0].includes('\t') ? '\t' : ',';

    const headers = lines[0]
      .split(delimiter)
      .map(h => h.replace(/"/g, '').trim().toLowerCase());

    const colIndex = {
      item_code: headers.findIndex(h => h.includes('item')),
      name: headers.findIndex(h => h.includes('name')),
      net_weight: headers.findIndex(h => h.includes('net')),
      uom: headers.findIndex(h => h.includes('uom')),
      test_type: headers.findIndex(h => h.includes('test')),
    };

    if (
      colIndex.item_code === -1 ||
      colIndex.name === -1 ||
      colIndex.net_weight === -1 ||
      colIndex.uom === -1
    ) {
      alert('Invalid CSV format. Required headers: Item Code, Name, Net Weight, UOM (Test Type is optional)');
      return [];
    }

    return lines.slice(1).map(line => {
      const cols = line.split(delimiter).map(v => v.replace(/"/g, '').trim());

      return {
        item_code: cols[colIndex.item_code],
        name: cols[colIndex.name],          // ✅ commas allowed
        net_weight_per_unit: cols[colIndex.net_weight],
        uom: cols[colIndex.uom],
        test_type: colIndex.test_type !== -1 ? cols[colIndex.test_type] : '',
      };
    }).filter(row => row.item_code && row.name);
  };

  const handleCsvFile = (file) => {
  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: true,

    complete: async (result) => {
      try {
        const rows = result.data;

        if (!rows || !rows.length) {
          alert("CSV file is empty");
          return;
        }

        // Validate headers
        const requiredHeaders = [
          "Item Code",
          "Name",
          "Net Weight",
          "UOM"
        ];

        const fileHeaders = Object.keys(rows[0]).map(h =>
          h.toLowerCase().trim()
        );

        const missing = requiredHeaders.filter(
          h => !fileHeaders.includes(h.toLowerCase())
        );

        if (missing.length) {
          alert(
            "Missing required columns:\n" +
            missing.join(", ") +
            "\n\nExpected headers:\n" +
            requiredHeaders.join(", ")
          );
          return;
        }

        // Map rows correctly
        const payload = rows.map(row => ({
          item_code: String(row["Item Code"]).trim(),
          name: String(row["Name"]).trim(),
          net_weight_per_unit: Number(row["Net Weight"]) || 0,
          uom: String(row["UOM"]).trim(),
          test_type: row["Test Type"] ? String(row["Test Type"]).trim() : '',
        })).filter(r => r.item_code && r.name);

        if (!payload.length) {
          alert("No valid records found.");
          return;
        }

        const response = await apiFetch(
          "/api/item-master/import-item-master",
          {
            method: "POST",
            body: JSON.stringify({ items: payload }),
          }
        );

        if (response.success) {
          alert("CSV imported successfully");
          setShowCsvModal(false);
          fetchItems();
        } else {
          alert(response.message || "Import failed");
        }
      } catch (err) {
        console.error(err);
        alert("CSV import failed");
      }
    },

    error: (err) => {
      console.error(err);
      alert("CSV parsing failed");
    },
  });
};

  /* ================= UI ================= */
  if (loading) {
    return (
      <Layout>
        <h2>Loading items...</h2>
      </Layout>
    );
  }

  const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

const exportToExcel = () => {
  const exportData = filteredRows.map((item) => ({
    ID: item.id,
    "Material Code": item.item_code,
    Name: item.name || item.item_name,
    "Net Weight": item.net_weight_per_unit || "",
    UOM: item.uom,
    "Test Type": item.test_type,
    Status: item.status,
    Created: formatDate(item.created_at),
    Updated: formatDate(item.updated_at)
  }));

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Materials");

  const excelBuffer = XLSX.write(workbook, {  
    bookType: "xlsx",
    type: "array"
  });

  const data = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });

  saveAs(data, "materials_all.xlsx");
};


function EllipsisTooltip({ text, index }) {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setShowTooltip(el.scrollWidth > el.clientWidth);
    }
  }, [text]);

  const tooltipPosition = index <= 1 ? "tooltip-bottom" : "tooltip-top";

  return (
    <td className="tooltip-cell">
      <span ref={textRef} className="ellipsis">
        {text}
      </span>

      {showTooltip && (
        <span className={`tooltip-text ${tooltipPosition}`}>
          {text}
        </span>
      )}
    </td>
  );
}

  return (
    <Layout>
      {/* HEADER */}
      <div className="products-toolbar">
        <div>
          <h1 className="products-title">Materials</h1>
          <p className="products-subtitle">Item master list</p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" onClick={exportToExcel}>
            Export All
          </button>
          <button className="btn-secondary" onClick={() => setShowCsvModal(true)}>
            Import CSV
          </button>
          <button className="btn-primary" onClick={openCreateModal}>
            Create Material
          </button>
        </div>
      </div>
      {/* TABLE */}
        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    Material Code
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: invoiceFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowInvoiceFilter(!showInvoiceFilter)}
                      title="Filter by Material Code"
                    />
                  </div>
                  {showInvoiceFilter && (
                    <input
                      type="text"
                      placeholder="Filter Material Code..."
                      value={invoiceFilter}
                      onChange={handleInvoiceFilterChange}
                      onBlur={() => setTimeout(() => setShowInvoiceFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    Name
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: customerFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowCustomerFilter(!showCustomerFilter)}
                      title="Filter by Name"
                    />
                  </div>
                  {showCustomerFilter && (
                    <input
                      type="text"
                      placeholder="Filter Name..."
                      value={customerFilter}
                      onChange={handleCustomerFilterChange}
                      onBlur={() => setTimeout(() => setShowCustomerFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>Net Weight</th>
                <th>UOM</th>
                <th>Test Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
                {handlePaginatedUsers.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                  No Material found
                </td>
              </tr>
              ) : (
               handlePaginatedUsers?.map((item,index) => ( 
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.item_code}</td>
                  {/* <td>{item.name || item.item_name}</td> */}
                  <EllipsisTooltip
                        text={item.name || item.item_name}
                        index={index}
                      />
                  <td>{item.net_weight_per_unit}</td>
                  <td>{item.uom}</td>
                  <td>{item.test_type || 'N/A'}</td>
                  {/* TOGGLE */}
                  <td>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={item.status === 0}
                        onChange={() => handleStatusToggle(item)}
                      />
                      <span className="slider"></span>
                    </label>
                  </td>
                  <td><DateTimeText value={item.created_at} /></td>
                  <td><DateTimeText value={item.updated_at} /></td>
                  <td>
                    <button className="btn-link" onClick={() => openEditModal(item)}>
                      Edit
                    </button>
                  </td>
                </tr>
             ))
  )}
            </tbody>
          </table>
        </div>
         <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
      {/* CREATE / UPDATE MODAL */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            <h2>{isEdit ? 'Update Material' : 'Create Material'}</h2>
            <div className="form-group">
              <label>Item Code</label>
              <input name="item_code" value={formData.item_code} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Name</label>
              <input name="name" value={formData.name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Net Weight Per Unit</label>
              <input type="number" name="net_weight_per_unit" value={formData.net_weight_per_unit} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>UOM</label>
              <input name="uom" value={formData.uom} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Test Type</label>
              <input name="test_type" value={formData.test_type} onChange={handleChange} placeholder="Enter test type for this material" />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value={0}>Active</option>
                <option value={1}>Inactive</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* CSV IMPORT MODAL */}
      <CsvImportModal
        open={showCsvModal}
        title="Import Item Master CSV"
        loading={importing}
        onClose={() => setShowCsvModal(false)}
        onImport={handleCsvFile}
      />
    </Layout>
  );
}
